/**
 * The "Business Discussion" slide of a review — see migration 133.
 *
 * Six lists, asked for on the review form. Each starts with three lines and
 * takes more. The deck shows the slide only when at least one item is filled
 * in, and then only the lists that have something in them.
 */
export type DiscussionKey =
  | 'topics'
  | 'keyActivity'
  | 'otherTopics'
  | 'businessGoals'
  | 'businessChallenges'
  | 'itGoals';

export type BusinessDiscussion = Record<DiscussionKey, string[]>;

/** Lines a list starts with. The template has three under each heading. */
export const DISCUSSION_ITEMS = 3;

/** Lines a list can hold. A ceiling, not a target. */
export const MAX_DISCUSSION_ITEMS = 10;

/** The two columns of the slide, each with its three lists, in template order. */
export const DISCUSSION_COLUMNS: {
  heading: string;
  lists: { key: DiscussionKey; label: string }[];
}[] = [
  {
    heading: 'Discussion Topics',
    lists: [
      { key: 'topics', label: 'Topics' },
      { key: 'keyActivity', label: 'Key Activity' },
      { key: 'otherTopics', label: 'Other Topics for Discussion' },
    ],
  },
  {
    heading: 'Strategic Overview',
    lists: [
      { key: 'businessGoals', label: 'Business Goals' },
      { key: 'businessChallenges', label: 'Business Challenges' },
      { key: 'itGoals', label: 'IT Goals' },
    ],
  },
];

const KEYS = DISCUSSION_COLUMNS.flatMap((c) => c.lists.map((l) => l.key));

/** Every list with its three starting slots, for the form. */
export const emptyDiscussion = (): BusinessDiscussion =>
  Object.fromEntries(
    KEYS.map((k) => [k, Array<string>(DISCUSSION_ITEMS).fill('')]),
  ) as BusinessDiscussion;

/** Never throws: a malformed value reads as nothing to discuss. Padded to at
 *  least three slots so the form always has its boxes, and keeping any lines
 *  added beyond them. */
export function parseDiscussion(raw: string | null | undefined): BusinessDiscussion {
  const out = emptyDiscussion();
  if (!raw) return out;
  try {
    const parsed = JSON.parse(raw) as Partial<Record<DiscussionKey, unknown>>;
    for (const k of KEYS) {
      const list = Array.isArray(parsed?.[k]) ? (parsed[k] as unknown[]) : [];
      const kept = list.slice(0, MAX_DISCUSSION_ITEMS).map((v) => (typeof v === 'string' ? v : ''));
      while (kept.length < DISCUSSION_ITEMS) kept.push('');
      out[k] = kept;
    }
  } catch {
    /* fall through to empty */
  }
  return out;
}

/** The filled-in items of one list. */
export const filled = (list: string[]) => list.map((i) => i.trim()).filter(Boolean);
